import React, { useState, useEffect } from 'react';
import { Ship, Download, Search, ChevronUp, ChevronDown } from 'lucide-react';
import type ExcelJS from 'exceljs';
import { exportSimpleList } from '../utils/excelHelper';
import * as api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AvatarEditor from '../components/AvatarEditor';
import CsvImportModal from '../components/CsvImportModal';
import MasterLinkButton from '../components/MasterLinkButton';
import { usePager } from '../components/TableTools';
import VesselDetailDrawer from '../components/VesselDetailDrawer';

const VESSEL_EXPORT_COLS: { label: string; width: number }[] = [
  { label: '船舶名',        width: 22 },
  { label: '種別',          width: 14 },
  { label: '船舶番号',      width: 12 },
  { label: 'IMO番号',       width: 14 },
  { label: '定員',          width: 8 },
  { label: '乗船中',        width: 8 },
  { label: '届出区分',      width: 12 },
  { label: '所有者名',      width: 20 },
  { label: '所有者住所',    width: 30 },
  { label: '登録日',        width: 12 },
];
const VESSEL_EXPORT_HEADER = VESSEL_EXPORT_COLS.map(c => c.label);
const VESSEL_TMPL_HEADER   = ['船名', '船種', 'IMO番号', '船籍', '定員'];
const VESSEL_TMPL_ROWS     = [
  ['第一丸', '貨物船',   'IMO1234567', 'JP', '20'],
  ['第二丸', 'タンカー', 'IMO2345678', 'JP', '15'],
];

/** 内部値（cargo等）で保存されている旧データを日本語ラベルへ変換 */
function vesselTypeLabel(t: string | null | undefined): string {
  if (!t) return '';
  return VESSEL_TYPES_FALLBACK.find(x => x.value === t)?.label ?? t;
}

function vesselToExportRow(v: Vessel): string[] {
  return [
    v.vessel_name,
    vesselTypeLabel(v.vessel_type),
    v.vessel_no ?? '',
    v.imo_number ?? '',
    v.capacity != null ? `${v.capacity}名` : '',
    v.crew_count != null ? `${v.crew_count}名` : '',
    OWNER_TYPES.find(o => o.value === (v.owner_type ?? 'self'))?.label ?? '',
    v.owner_name ?? '',
    v.owner_address ?? '',
    v.created_at?.slice(0, 10) ?? '',
  ];
}

function exportCsvFile(rows: string[][], header: string[], filename: string) {
  const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
async function exportExcelFile(rows: string[][], filename: string) {
  const today = new Date().toISOString().slice(0, 10);
  await exportSimpleList(VESSEL_EXPORT_COLS, rows, '船舶一覧', filename, `船舶一覧（${today} 時点 / ${rows.length}隻）`);
}

interface Vessel {
  id: string;
  vessel_name: string;
  vessel_type: string;
  capacity: number;
  long_break_hours: number;
  image_path?: string | null;
  company_id: string;
  created_at: string;
  crew_count?: number;
  owner_type?: string;
  owner_name?: string | null;
  owner_address?: string | null;
  vessel_no?: string | null;
  gross_tonnage?: string | null;
  port_of_registry?: string | null;
  main_engine_type?: string | null;
  main_engine_count?: string | null;
  main_engine_power?: string | null;
  imo_number?: string | null;
}

const OWNER_TYPES = [
  { value: 'self',      label: '自社' },
  { value: 'owner',     label: '船舶所有者' },
  { value: 'manager',   label: '船舶管理人' },
  { value: 'charterer', label: '船舶借入人' },
];

interface WorkType {
  id: string;
  name: string;
  emoji: string;
  display_order: number;
  requires_alcohol: number;
}

interface WorkTypeMasterItem {
  id: string;
  name: string;
  emoji: string;
  requires_alcohol: number;
  is_active: number;
}

interface ActivityTypeMaster {
  id: string;
  name: string;
  color: string;
  is_active: number;
}

const VESSEL_TYPES_FALLBACK = [
  { value: 'cargo',     label: '貨物船' },
  { value: 'tanker',    label: 'タンカー' },
  { value: 'passenger', label: '旅客船' },
  { value: 'fishing',   label: '漁船' },
  { value: 'other',     label: 'その他' },
];

const EMPTY_FORM = { vessel_name: '', vessel_type: '', capacity: 20, long_break_hours: 4, owner_type: 'self', owner_name: '', owner_address: '', vessel_no: '', imo_number: '', gross_tonnage: '', port_of_registry: '', main_engine_type: '', main_engine_count: '', main_engine_power: '' };

const VESSEL_API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
/** 船舶画像をアップロード（登録/編集フォームから使用） */
async function uploadVesselImage(vesselId: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('image', file);
  const r = await fetch(`${VESSEL_API_BASE}/vessels/${vesselId}/image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': window.location.hostname.split('.')[0] ?? 'unison-tech' },
    body: fd,
  });
  if (!r.ok) throw new Error('vessel image upload failed');
}

// ── マスタ選択セクション（業務種別 + 特別作業）──────────────────────────────
const MasterSelectionSections: React.FC<{
  workTypeMasters: WorkTypeMasterItem[];
  activityTypeMasters: ActivityTypeMaster[];
  selectedWorkTypeIds: string[];
  selectedActivityTypeIds: string[];
  onWorkTypeChange: (ids: string[]) => void;
  onActivityTypeChange: (ids: string[]) => void;
  /** マスタに無い、この船だけの業務種別（編集画面から外せるように一覧に出す） */
  customWorkTypes?: WorkType[];
  onRemoveCustomWorkType?: (wt: WorkType) => void;
}> = ({ workTypeMasters, activityTypeMasters, selectedWorkTypeIds, selectedActivityTypeIds, onWorkTypeChange, onActivityTypeChange, customWorkTypes = [], onRemoveCustomWorkType }) => (
  <>
    {workTypeMasters.length > 0 && (
      <div className="form-g">
        <label className="form-l" style={{ alignSelf: 'flex-start', paddingTop: 6 }}>業務種別</label>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>マスタから使用する業務種別を選択</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto',
            border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
            {workTypeMasters.map(m => (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                borderRadius: 6, cursor: 'pointer', userSelect: 'none',
                background: selectedWorkTypeIds.includes(m.id) ? '#e0f2fe' : 'transparent' }}>
                <input type="checkbox" checked={selectedWorkTypeIds.includes(m.id)}
                  onChange={e => onWorkTypeChange(
                    e.target.checked ? [...selectedWorkTypeIds, m.id] : selectedWorkTypeIds.filter(id => id !== m.id)
                  )}
                  style={{ accentColor: '#0ea5e9', cursor: 'pointer' }} />
                <span style={{ fontSize: 17 }}>{m.emoji}</span>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{m.name}</span>
                {m.requires_alcohol === 1 && (
                  <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7',
                    border: '1px solid #fde68a', borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                    アルコール
                  </span>
                )}
              </label>
            ))}
            {/* マスタに無い、この船だけの項目。チェックでは扱えないので個別に外す */}
            {customWorkTypes.map(wt => (
              <div key={wt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, background: '#fffbeb' }}>
                <span style={{ fontSize: 17 }}>{wt.emoji}</span>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{wt.name}</span>
                <span style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                  この船のみ
                </span>
                {onRemoveCustomWorkType && (
                  <button type="button" onClick={() => onRemoveCustomWorkType(wt)}
                    style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fff', border: '1px solid #fecaca', borderRadius: 5, padding: '2px 8px', cursor: 'pointer' }}>
                    削除
                  </button>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
            <button type="button" onClick={() => onWorkTypeChange(workTypeMasters.map(m => m.id))}
              style={{ fontSize: 11, color: '#0369a1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              全選択
            </button>
            <button type="button" onClick={() => onWorkTypeChange([])}
              style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              全解除
            </button>
          </div>
        </div>
      </div>
    )}

    {activityTypeMasters.length > 0 && (
      <div className="form-g">
        <label className="form-l" style={{ alignSelf: 'flex-start', paddingTop: 6 }}>特別作業</label>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>マスタから使用する特別作業を選択</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 150, overflowY: 'auto',
            border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
            {activityTypeMasters.map(m => (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                borderRadius: 6, cursor: 'pointer', userSelect: 'none',
                background: selectedActivityTypeIds.includes(m.id) ? '#f3e8ff' : 'transparent' }}>
                <input type="checkbox" checked={selectedActivityTypeIds.includes(m.id)}
                  onChange={e => onActivityTypeChange(
                    e.target.checked ? [...selectedActivityTypeIds, m.id] : selectedActivityTypeIds.filter(id => id !== m.id)
                  )}
                  style={{ accentColor: '#7c3aed', cursor: 'pointer' }} />
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                  background: m.color ?? '#6366f1', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
            <button type="button" onClick={() => onActivityTypeChange(activityTypeMasters.map(m => m.id))}
              style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              全選択
            </button>
            <button type="button" onClick={() => onActivityTypeChange([])}
              style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              全解除
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);

// ── メインページ ─────────────────────────────────────────────────────────────
const VesselsPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const [vessels, setVessels]           = useState<Vessel[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [editTarget, setEditTarget]     = useState<Vessel | null>(null);
  const [detailId, setDetailId]         = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [error, setError]               = useState('');
  const [saving, setSaving]             = useState(false);
  const [vImgFile, setVImgFile]         = useState<File | null>(null);
  const [vImgPreview, setVImgPreview]   = useState<string>('');

  // マスタ
  const [vesselTypeMasters, setVesselTypeMasters] = useState<{ id: string; name: string }[]>([]);
  const [workTypeMasters, setWorkTypeMasters]     = useState<WorkTypeMasterItem[]>([]);
  const [activityTypeMasters, setActivityTypeMasters] = useState<ActivityTypeMaster[]>([]);

  // モーダル内の選択状態（新規・編集共用）
  const [selectedWorkTypeIds, setSelectedWorkTypeIds]     = useState<string[]>([]);
  const [selectedActivityTypeIds, setSelectedActivityTypeIds] = useState<string[]>([]);
  // 編集時の変更検知用（元の状態を保持）
  const [origWorkTypes, setOrigWorkTypes]         = useState<WorkType[]>([]);
  const [origActivityTypeIds, setOrigActivityTypeIds] = useState<string[]>([]);

  const [showImport,     setShowImport]     = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter]   = useState('');
  const [boardFilter, setBoardFilter] = useState('');   // '' | 'onboard' | 'empty'
  const [sortField, setSortField]     = useState('vessel_name');
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('asc');

  const fetchVessels = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/vessels');
      setVessels(res?.vessels ?? res?.data ?? []);
    } catch { setVessels([]); }
    setLoading(false);
  };

  useEffect(() => {
    fetchVessels();
    api.get<any>('/vessel-type-masters')
      .then(res => setVesselTypeMasters(res?.data?.filter((v: any) => v.is_active) ?? []))
      .catch(() => {});
    api.get<any>('/work-type-masters')
      .then(res => setWorkTypeMasters((res?.data ?? []).filter((m: any) => m.is_active)))
      .catch(() => {});
    api.get<any>('/activity-masters')
      .then(res => setActivityTypeMasters((res?.data ?? []).filter((m: any) => m.is_active)))
      .catch(() => {});
  }, []);

  // ── 新規登録 ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setError('');
    setSelectedWorkTypeIds([]);
    setSelectedActivityTypeIds([]);
    setOrigWorkTypes([]);
    setOrigActivityTypeIds([]);
    setVImgFile(null); setVImgPreview('');
    setShowModal(true);
  };

  // ── 編集（既存の業務種別・特別作業を取得して選択状態を初期化）───────────
  const openEdit = async (v: Vessel) => {
    setEditTarget(v);
    setForm({ vessel_name: v.vessel_name, vessel_type: v.vessel_type ?? '', capacity: v.capacity ?? 0, long_break_hours: v.long_break_hours ?? 4, owner_type: v.owner_type ?? 'self', owner_name: v.owner_name ?? '', owner_address: v.owner_address ?? '', vessel_no: v.vessel_no ?? '', imo_number: v.imo_number ?? '', gross_tonnage: v.gross_tonnage ?? '', port_of_registry: v.port_of_registry ?? '', main_engine_type: v.main_engine_type ?? '', main_engine_count: v.main_engine_count ?? '', main_engine_power: v.main_engine_power ?? '' });
    setError('');
    setVImgFile(null); setVImgPreview('');
    setSelectedWorkTypeIds([]);
    setSelectedActivityTypeIds([]);

    // 現在の業務種別を取得してマスタIDに対応付け
    try {
      const wtRes = await api.get<any>(`/vessels/${v.id}/work-types`);
      const current: WorkType[] = wtRes?.data ?? [];
      setOrigWorkTypes(current);
      const matchedIds = current
        .map(wt => workTypeMasters.find(m => m.name === wt.name)?.id)
        .filter((id): id is string => Boolean(id));
      setSelectedWorkTypeIds(matchedIds);
    } catch {
      setOrigWorkTypes([]);
    }

    // 現在の特別作業を取得
    try {
      const atRes = await api.get<any>(`/vessels/${v.id}/activity-types`);
      const atData: Array<{ master: { id: string }; is_active: number }> = atRes?.data ?? [];
      const activeIds = atData.filter(x => x.is_active === 1).map(x => x.master.id);
      setOrigActivityTypeIds(activeIds);
      setSelectedActivityTypeIds(activeIds);
    } catch {
      setOrigActivityTypeIds([]);
    }

    setShowModal(true);
  };

  // ── 保存 ─────────────────────────────────────────────────────────────────
  const save = async () => {
    setError('');
    if (!form.vessel_name) { setError('船舶名を入力してください'); return; }
    setSaving(true);
    try {
      if (editTarget) {
        await api.put('/vessels/' + editTarget.id, { ...form });
        if (vImgFile) { try { await uploadVesselImage(editTarget.id, vImgFile); } catch { /* 画像失敗は無視 */ } }

        // 業務種別の同期：名前ベースの差分で追加・削除する。
        // マスタに存在しない項目（船に直接追加されたもの）は勝手に消さず温存する。
        const selectedNames = new Set(
          selectedWorkTypeIds.map(mid => workTypeMasters.find(x => x.id === mid)?.name).filter((n): n is string => Boolean(n))
        );
        const masterNames = new Set(workTypeMasters.map(m => m.name));
        for (const wt of origWorkTypes) {
          // マスタ由来なのにチェックが外れたものだけ削除（マスタに無い独自項目は温存）
          if (masterNames.has(wt.name) && !selectedNames.has(wt.name)) {
            await api.del(`/vessels/${editTarget.id}/work-types/${wt.id}`).catch(() => {});
          }
        }
        const origNames = new Set(origWorkTypes.map(wt => wt.name));
        for (const mid of selectedWorkTypeIds) {
          const m = workTypeMasters.find(x => x.id === mid);
          if (m && !origNames.has(m.name)) {
            await api.post(`/vessels/${editTarget.id}/work-types`, { name: m.name, emoji: m.emoji, requires_alcohol: m.requires_alcohol }).catch(() => {});
          }
        }

        // 特別作業の同期：追加分 / 削除分を差分で処理
        const toAdd    = selectedActivityTypeIds.filter(id => !origActivityTypeIds.includes(id));
        const toRemove = origActivityTypeIds.filter(id => !selectedActivityTypeIds.includes(id));
        for (const id of toAdd) {
          await api.post(`/vessels/${editTarget.id}/activity-types`, { activity_master_id: id }).catch(() => {});
        }
        for (const id of toRemove) {
          await api.del(`/vessels/${editTarget.id}/activity-types/${id}`).catch(() => {});
        }
      } else {
        const res = await api.post<any>('/vessels', { ...form, company_id: authUser?.company_id ?? '' });
        const newId: string | undefined = res?.data?.id;
        if (newId) {
          if (vImgFile) { try { await uploadVesselImage(newId, vImgFile); } catch { /* 画像失敗は無視 */ } }
          for (const mid of selectedWorkTypeIds) {
            const m = workTypeMasters.find(x => x.id === mid);
            if (m) await api.post(`/vessels/${newId}/work-types`, { name: m.name, emoji: m.emoji, requires_alcohol: m.requires_alcohol }).catch(() => {});
          }
          for (const mid of selectedActivityTypeIds) {
            await api.post(`/vessels/${newId}/activity-types`, { activity_master_id: mid }).catch(() => {});
          }
        }
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditTarget(null);
      fetchVessels();
    } catch (e: any) {
      setError(e.message ?? '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try { await api.del('/vessels/' + deleteConfirmId); setDeleteConfirmId(null); fetchVessels(); }
    catch (e: any) { alert('削除に失敗しました: ' + (e.message ?? '')); setDeleteConfirmId(null); }
  };

  const deleteTarget = vessels.find(v => v.id === deleteConfirmId);

  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (field: string) => sortField === field
    ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
    : <ChevronUp size={12} style={{ opacity: 0.3 }} />;

  // フィルタ用の船種候補（実データに存在する種別）
  const typeOptions = Array.from(new Set(vessels.map(v => v.vessel_type).filter(Boolean))) as string[];

  const filtered = vessels
    .filter(v => {
      if (typeFilter && v.vessel_type !== typeFilter) return false;
      if (boardFilter === 'onboard' && !((v.crew_count ?? 0) > 0)) return false;
      if (boardFilter === 'empty' && (v.crew_count ?? 0) > 0) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return v.vessel_name.toLowerCase().includes(q) || (v.vessel_type ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const va = String((a as any)[sortField] ?? '');
      const vb = String((b as any)[sortField] ?? '');
      const cmp = va.localeCompare(vb, 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // フィルタ・ソート適用後の配列をページング（CSV/Excel出力は vessels 全件のまま）
  const pager = usePager(filtered, { noun: '隻' });

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">船舶管理</h1>
        <div className="flex-row">
          <MasterLinkButton tab="vessel-type" title="船舶種類・寄港地などのマスタを開きます" />
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              type="text"
              className="form-i"
              placeholder="船舶名・種別で検索..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 30, width: 200 }}
            />
          </div>
          <select className="form-i" style={{ width: 150 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">全種別</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="form-i" style={{ width: 150 }} value={boardFilter} onChange={e => setBoardFilter(e.target.value)}>
            <option value="">乗船状況（全て）</option>
            <option value="onboard">⚓ 乗船あり</option>
            <option value="empty">空き（乗船なし）</option>
          </select>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowExportMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#374151', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Download size={14} /> データ ▾
            </button>
            {showExportMenu && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 210, overflow: 'hidden' }}
                onMouseLeave={() => setShowExportMenu(false)}>
                <button onClick={() => { exportCsvFile(vessels.map(vesselToExportRow), VESSEL_EXPORT_HEADER, '船舶一覧.csv'); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                  CSVダウンロード (.csv)
                </button>
                <button onClick={() => { exportExcelFile(vessels.map(vesselToExportRow), '船舶一覧.xlsx').catch(e => { console.error(e); alert('Excelの生成に失敗しました。ページを再読み込みしてから再度お試しください。'); }); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                  Excelダウンロード (.xlsx)
                </button>
                <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                <button onClick={() => { exportCsvFile(VESSEL_TMPL_ROWS, VESSEL_TMPL_HEADER, '船舶_インポートテンプレート.csv'); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1', textAlign: 'left' }}>
                  テンプレートDL (.csv)
                </button>
                <button onClick={() => { setShowImport(true); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1', textAlign: 'left' }}>
                  CSVインポート
                </button>
              </div>
            )}
          </div>
          <button className="btn-p" onClick={openCreate}>+ 新規登録</button>
        </div>
      </div>

      {!loading && pager.renderBar('top')}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            {searchQuery ? `「${searchQuery}」に一致する船舶が見つかりません`
              : (typeFilter || boardFilter) ? '条件に一致する船舶が見つかりません'
              : 'データがありません'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('vessel_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>船舶名 {sortIcon('vessel_name')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('vessel_type')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>種別 {sortIcon('vessel_type')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('capacity')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>定員 {sortIcon('capacity')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('crew_count')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>乗船 {sortIcon('crew_count')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('created_at')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>登録日 {sortIcon('created_at')}</span>
                </th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pager.pageRows.map(v => {
                const certFilled = [v.vessel_no, v.gross_tonnage, v.port_of_registry, v.main_engine_type, v.main_engine_power].filter(Boolean).length;
                const certComplete = certFilled === 5;
                const onBoard = v.crew_count ?? 0;
                return (
                <tr key={v.id} onClick={() => setDetailId(v.id)} style={{ cursor: 'pointer' }} title="クリックで詳細を表示"
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                        <AvatarEditor
                          entityId={v.id}
                          type="vessel"
                          imagePath={v.image_path}
                          displayName={v.vessel_name}
                          size={36}
                          onUpdated={(path) => {
                            setVessels(prev => prev.map(x => x.id === v.id ? { ...x, image_path: path } : x));
                          }}
                        />
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Ship size={13} style={{ color: '#64748b' }} /> {v.vessel_name}
                        </span>
                        {certComplete
                          ? <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 20, padding: '1px 8px', fontWeight: 700, width: 'fit-content' }}>✓ 証明書情報 完備</span>
                          : <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', borderRadius: 20, padding: '1px 8px', fontWeight: 700, width: 'fit-content' }}>証明書情報 {certFilled}/5</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{v.vessel_type}</td>
                  <td style={{ fontSize: 12 }}>{v.capacity}名</td>
                  <td style={{ fontSize: 12 }}>
                    {onBoard > 0
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 20, padding: '2px 9px', fontWeight: 700 }}>⚓ {onBoard}名</span>
                      : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{v.created_at?.slice(0, 10) ?? '-'}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setDetailId(v.id)}
                        style={{ padding: '4px 10px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                        詳細
                      </button>
                      <button onClick={() => openEdit(v)}
                        style={{ padding: '4px 12px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        編集
                      </button>
                      <button onClick={() => setDeleteConfirmId(v.id)}
                        style={{ padding: '4px 12px', background: '#fff', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && pager.renderBar('bottom')}

      {/* ── 新規登録 / 編集モーダル ─────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 560, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, position: 'sticky', top: 0, background: '#fff', paddingBottom: 8, borderBottom: '1px solid #f1f5f9', zIndex: 1 }}>
              {editTarget ? '船舶編集' : '船舶登録'}
            </h2>
            {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}

            <div className="form-g">
              <label className="form-l">船舶名 *</label>
              <input className="form-i" value={form.vessel_name}
                onChange={e => setForm(f => ({ ...f, vessel_name: e.target.value }))} />
            </div>
            {/* 船舶画像（登録・編集時にそのまま設定できる） */}
            <div className="form-g">
              <label className="form-l">船舶画像</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 64, height: 48, borderRadius: 8, background: '#f1f5f9', border: '1.5px solid #e2e8f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {vImgPreview ? <img src={vImgPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20, color: '#cbd5e1' }}>🚢</span>}
                </div>
                <label style={{ padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#fff', color: '#374151' }}>
                  {vImgFile ? '画像を変更' : '画像を選択'}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) { setVImgFile(f); setVImgPreview(URL.createObjectURL(f)); } e.target.value = ''; }} />
                </label>
                {vImgFile && <button type="button" onClick={() => { setVImgFile(null); setVImgPreview(''); }} style={{ border: 'none', background: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>取消</button>}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>登録と同時に船舶画像が設定されます（後から変更も可）</div>
            </div>
            <div className="form-g">
              <label className="form-l">種別</label>
              <select className="form-i" value={form.vessel_type}
                onChange={e => setForm(f => ({ ...f, vessel_type: e.target.value }))}>
                <option value="">— 選択してください —</option>
                {vesselTypeMasters.length > 0
                  ? vesselTypeMasters.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
                  : VESSEL_TYPES_FALLBACK.map(t => <option key={t.value} value={t.value}>{t.label}</option>)
                }
              </select>
            </div>
            <div className="form-g">
              <label className="form-l">定員</label>
              <input className="form-i" type="number" value={form.capacity}
                onChange={e => setForm(f => ({ ...f, capacity: Number(e.target.value) }))} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', margin: '-2px 0 4px', display: 'flex', alignItems: 'center', gap: 5 }}>
              ℹ 長時間離席しきい値（アルコール検査の必須化）は<strong style={{ color: '#64748b' }}>マスタ管理 → しきい値</strong>で会社共通の設定になりました。
            </div>

            {/* 船舶識別番号（国土交通省様式用） */}
            <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>
              船舶識別番号
              <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>（乗組員名簿・国土交通省様式に使用）</span>
            </div>
            <div className="form-g">
              <label className="form-l">船舶番号</label>
              <input className="form-i" value={form.vessel_no}
                placeholder="例: 123456"
                onChange={e => setForm(f => ({ ...f, vessel_no: e.target.value }))} />
            </div>
            <div className="form-g">
              <label className="form-l">IMO番号</label>
              <input className="form-i" value={form.imo_number}
                placeholder="例: IMO1234567"
                onChange={e => setForm(f => ({ ...f, imo_number: e.target.value }))} />
            </div>

            {/* 証明書発行用の船舶情報 */}
            <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>
              証明書発行用の情報
              <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>（雇入届・航行報告書に自動反映）</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-g" style={{ flex: 1 }}>
                <label className="form-l">総トン数</label>
                <input className="form-i" value={form.gross_tonnage} placeholder="例: 499"
                  onChange={e => setForm(f => ({ ...f, gross_tonnage: e.target.value }))} />
              </div>
              <div className="form-g" style={{ flex: 1 }}>
                <label className="form-l">船籍港</label>
                <input className="form-i" value={form.port_of_registry} placeholder="例: 今治"
                  onChange={e => setForm(f => ({ ...f, port_of_registry: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-g" style={{ flex: 1.4 }}>
                <label className="form-l">主機の種類</label>
                <input className="form-i" value={form.main_engine_type} placeholder="例: ディーゼル（内燃）"
                  onChange={e => setForm(f => ({ ...f, main_engine_type: e.target.value }))} />
              </div>
              <div className="form-g" style={{ width: 80 }}>
                <label className="form-l">箇数</label>
                <input className="form-i" value={form.main_engine_count} placeholder="1"
                  onChange={e => setForm(f => ({ ...f, main_engine_count: e.target.value }))} />
              </div>
              <div className="form-g" style={{ flex: 1 }}>
                <label className="form-l">出力(kW)</label>
                <input className="form-i" value={form.main_engine_power} placeholder="例: 1235"
                  onChange={e => setForm(f => ({ ...f, main_engine_power: e.target.value }))} />
              </div>
            </div>

            {/* 所有者情報（国土交通省様式用） */}
            <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>
              所有者情報
              <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>（乗組員名簿・国土交通省様式に使用）</span>
            </div>
            <div className="form-g">
              <label className="form-l">届出区分</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {OWNER_TYPES.map(ot => (
                  <label key={ot.value} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, border: form.owner_type === ot.value ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0', background: form.owner_type === ot.value ? 'rgba(14,165,233,0.08)' : '#f8fafc', fontWeight: form.owner_type === ot.value ? 700 : 400 }}>
                    <input type="radio" name="owner_type" checked={form.owner_type === ot.value}
                      onChange={() => setForm(f => ({ ...f, owner_type: ot.value }))}
                      style={{ accentColor: '#0ea5e9' }} />
                    {ot.label}
                  </label>
                ))}
              </div>
            </div>
            {form.owner_type !== 'self' && (
              <>
                <div className="form-g">
                  <label className="form-l">{OWNER_TYPES.find(o => o.value === form.owner_type)?.label}名</label>
                  <input className="form-i" value={form.owner_name}
                    placeholder="法人名または氏名"
                    onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} />
                </div>
                <div className="form-g">
                  <label className="form-l">住所</label>
                  <input className="form-i" value={form.owner_address}
                    placeholder="住所"
                    onChange={e => setForm(f => ({ ...f, owner_address: e.target.value }))} />
                </div>
              </>
            )}

            {/* 業務種別 + 特別作業（マスタから選択）*/}
            <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>
              業務・特別作業の設定
            </div>
            <MasterSelectionSections
              customWorkTypes={origWorkTypes.filter(wt => !workTypeMasters.some(m => m.name === wt.name))}
              onRemoveCustomWorkType={async wt => {
                if (!editTarget) return;
                if (!window.confirm(`この船の業務種別から「${wt.name}」を外しますか？\n（過去の勤怠記録は残ります）`)) return;
                try {
                  await api.del(`/vessels/${editTarget.id}/work-types/${wt.id}`);
                  setOrigWorkTypes(prev => prev.filter(x => x.id !== wt.id));
                } catch (e: any) { alert(e?.message ?? '削除に失敗しました'); }
              }}
              workTypeMasters={workTypeMasters}
              activityTypeMasters={activityTypeMasters}
              selectedWorkTypeIds={selectedWorkTypeIds}
              selectedActivityTypeIds={selectedActivityTypeIds}
              onWorkTypeChange={setSelectedWorkTypeIds}
              onActivityTypeChange={setSelectedActivityTypeIds}
            />

            <div className="flex-row" style={{ justifyContent: 'flex-end', marginTop: 20, gap: 8 }}>
              <button className="btn-o" onClick={() => setShowModal(false)}>キャンセル</button>
              <button className="btn-p" onClick={save} disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 削除確認ダイアログ ───────────────────────────────────── */}
      {deleteConfirmId && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>船舶の削除</h2>
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 20 }}>
              <strong>{deleteTarget?.vessel_name}</strong> を削除しますか？<br />
              <span style={{ fontSize: 12, color: '#6b7280' }}>この操作は取り消せません。</span>
            </p>
            <div className="flex-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-o" onClick={() => setDeleteConfirmId(null)}>キャンセル</button>
              <button onClick={confirmDelete}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                削除する
              </button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <CsvImportModal
          title="船舶"
          endpoint="/vessels/bulk-import"
          bodyKey="vessels"
          columns={[
            { key: 'vessel_name',      label: '船名',         required: true,  hint: '' },
            { key: 'vessel_type',      label: '船種',         required: false, hint: '貨物船 / タンカー / コンテナ船など（省略時: 貨物船）' },
            { key: 'imo_number',       label: 'IMO番号',      required: false, hint: 'IMO1234567 形式' },
            { key: 'flag',             label: '船籍',         required: false, hint: 'JP など（省略時: JP）' },
            { key: 'capacity',         label: '定員',         required: false, hint: '数値' },
          ]}
          templateRows={[
            ['第一丸', '貨物船',   'IMO1234567', 'JP', '20'],
            ['第二丸', 'タンカー', 'IMO2345678', 'JP', '15'],
            ['第三丸', 'コンテナ船', '',         'JP', '25'],
          ]}
          onClose={() => setShowImport(false)}
          onComplete={fetchVessels}
        />
      )}

      {/* 船舶詳細ドロワー（人材管理と共通の体験）*/}
      {detailId && (
        <VesselDetailDrawer
          vesselId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={(id) => { const v = vessels.find(x => x.id === id); setDetailId(null); if (v) openEdit(v); }}
          onChanged={fetchVessels}
        />
      )}
    </div>
  );
};

export default VesselsPage;
